import React, { useMemo } from 'react';
import { useSubscriptionPackages } from '../../hooks/useSubscriptionPackages';
import { PaymentModal } from '../payment/PaymentModal';
import { BillingToggle, PlanCard, isPopularPlan } from './PlanCard';

// Simple Button component
const Button: React.FC<{
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  variant?: 'default' | 'outline';
  className?: string;
  disabled?: boolean;
}> = ({ children, onClick, variant = 'default', className = '', disabled = false }) => {
  const baseClasses = 'px-4 py-2 rounded-md font-medium transition-colors';
  const variantClasses = variant === 'outline'
    ? 'border border-gray-300 text-gray-700 hover:bg-gray-50'
    : 'text-white hover:bg-green-600';
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses} ${disabledClasses} ${className}`}
      style={{ backgroundColor: disabled ? '#a0aec0' : '#4CAF50' }}
    >
      {children}
    </button>
  );
};

export interface SmartPackageSelectorProps {
  /** Context for filtering packages - track name, state, or category */
  context?: {
    trackName?: string;
    trackId?: string;
    trackState?: string;
    category?: 'metro' | 'provincial' | 'country';
    meetingDate?: string;
  };
  /** Title to display above packages */
  title?: string;
  /** Description text */
  description?: string;
  /** Callback when user subscribes to packages */
  onSubscribe?: () => void;
  /** Compact mode for smaller displays */
  compact?: boolean;
  /** Maximum number of packages to show */
  maxPackages?: number;
}

const getPackageColor = (packageCode: string): string => {
  // Use a more flexible approach based on package type/category patterns
  const code = packageCode.toUpperCase();
  
  if (code.includes('EVERYTHING') || code.includes('ALL')) return 'border-green-600 bg-green-50';
  if (code.includes('METRO') || code.includes('CITY')) return 'border-green-500 bg-green-50';
  if (code.includes('TEST') || code.includes('TRIAL')) return 'border-blue-400 bg-blue-50';
  if (code.includes('PREMIUM') || code.includes('VIP')) return 'border-purple-400 bg-purple-50';
  
  // Default green theme for most packages
  return 'border-green-400 bg-green-50';
};

const getPackageIcon = (packageCode: string): string => {
  // Keep original hardcoded icons for existing packages, add pattern matching for new ones
  switch (packageCode) {
    case 'ONE_TRACK': return '🎯';
    case 'METRO': return '🏙️';
    case 'PROVINCIAL': return '🌲';
    case 'COUNTRY': return '🌾';
    case 'EVERYTHING': return '⭐';
    default: {
      // Pattern matching for new packages only
      const code = packageCode.toUpperCase();
      if (code.includes('TEST') || code.includes('TRIAL')) return '🧪';
      if (code.includes('PREMIUM') || code.includes('VIP')) return '💎';
      return '📦';
    }
  }
};

const getPackagePriority = (packageCode: string, context?: SmartPackageSelectorProps['context']): number => {
  // Simplified priority system - mainly for context-based boosting
  // The database displayOrder should be the primary sorting mechanism
  let priority = 0; // Default priority - let displayOrder handle sorting
  
  // Only boost priority based on context relevance
  if (context?.category) {
    const code = packageCode.toUpperCase();
    if (context.category === 'metro' && (code.includes('METRO') || code.includes('CITY'))) priority += 10;
    if (context.category === 'provincial' && (code.includes('PROVINCIAL') || code.includes('STATE'))) priority += 10;
    if (context.category === 'country' && (code.includes('COUNTRY') || code.includes('NATIONAL'))) priority += 10;
  }

  return priority;
};

export const SmartPackageSelector: React.FC<SmartPackageSelectorProps> = ({
  context,
  title = "Choose Your Package",
  description = "Select a subscription package to access racing tips and analysis.",
  onSubscribe,
  compact = false,
  maxPackages = 5
}) => {
  const {
    packages,
    userSubscriptions,
    loading,
    error,
    selectedPackages,
    billingCycle,
    setBillingCycle,
    togglePackageSelection,
    subscribeToPackages,
    isPackageSubscribed,
    hasMultiPackageDiscount,
    getDiscountMessage,
    isPaymentModalOpen,
    setIsPaymentModalOpen,
    handlePaymentSuccess,
    showEverythingNotification,
    setShowEverythingNotification,
    getEverythingPackagePrice,
    setSelectedPackages
  } = useSubscriptionPackages(context?.meetingDate);


  // Filter and sort packages intelligently - separate available and owned packages
  const { availablePackages, ownedSubscriptions, hasSubscriptionPackages, hasOneTimePackages } = useMemo(() => {
    if (!packages.length) return { availablePackages: [], ownedSubscriptions: [], hasSubscriptionPackages: false, hasOneTimePackages: false };

    // Show ALL packages returned by GraphQL query - no filtering
    // The backend getAvailablePackages query handles all filtering logic
    // Create a copy of the array since GraphQL data is read-only
    let filteredAvailable = [...packages];

    // Sort by database display order first, then by context-based priority
    const sortPackages = (pkgs: any[]) => pkgs.sort((a: any, b: any) => {
      const priorityA = getPackagePriority(a.packageCode, context);
      const priorityB = getPackagePriority(b.packageCode, context);
      
      // If one has context priority boost and other doesn't, prioritize the boosted one
      if (priorityA > 0 && priorityB === 0) return -1;
      if (priorityB > 0 && priorityA === 0) return 1;
      
      // Otherwise, sort by database display order (primary sorting)
      return (a.displayOrder || 999) - (b.displayOrder || 999);
    });

    // Sort subscriptions by creation date (newest first)
    const sortedSubscriptions = userSubscriptions
      .filter((sub: any) => sub.isActive)
      .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const sortedAvailable = sortPackages(filteredAvailable);

    // Check billing model types in available packages
    // Handle both GraphQL enum strings and potential lowercase variants
    const hasSubscriptionPackages = sortedAvailable.some((pkg: any) =>
      pkg.billingModel === 'subscription' || pkg.billingModel === 'SUBSCRIPTION'
    );
    const hasOneTimePackages = sortedAvailable.some((pkg: any) =>
      pkg.billingModel === 'one_time' || pkg.billingModel === 'ONE_TIME'
    );

    return {
      availablePackages: sortedAvailable,
      ownedSubscriptions: sortedSubscriptions,
      hasSubscriptionPackages,
      hasOneTimePackages
    };
  }, [packages, userSubscriptions, context, maxPackages]); // Removed isPackageSubscribed to prevent recalculation

  // Keep relevantPackages for backward compatibility
  const relevantPackages = availablePackages;

  const handleSubscribeClick = () => {
    subscribeToPackages(); // This now just opens the PaymentModal
  };

  const handleModalPaymentSuccess = () => {
    handlePaymentSuccess(); // This handles post-payment cleanup
    onSubscribe?.(); // Call the optional callback
  };

  // Calculate total amount for PaymentModal - handle mixed billing models
  const totalAmount = relevantPackages
    .filter((pkg: any) => selectedPackages.includes(pkg.id))
    .reduce((total: number, pkg: any) => {
      if (pkg.billingModel === 'one_time' || pkg.billingModel === 'ONE_TIME') {
        return total + pkg.priceOneTime;
      } else {
        return total + (billingCycle === 'WEEKLY' ? pkg.priceWeekly : pkg.priceMonthly);
      }
    }, 0);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600 text-sm">Error loading packages: {error}</p>
      </div>
    );
  }

  if (relevantPackages.length === 0 && ownedSubscriptions.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-gray-600 text-sm">No packages available.</p>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold mb-3" style={{ color: '#2d3748' }}>{title}</h3>
        <p className="text-base" style={{ color: '#4a5568' }}>{description}</p>
        {context?.trackName && (
          <p className="text-sm mt-2 font-medium" style={{ color: '#4CAF50' }}>
            Access to {context.trackName} requires one of these packages:
          </p>
        )}
      </div>

      {/* Billing Cycle Toggle - Only show if there are subscription packages */}
      {hasSubscriptionPackages && (
        <div className="mb-10">
          <BillingToggle cycle={billingCycle} onChange={setBillingCycle} />
        </div>
      )}

      {/* Everything Package Suggestion Popup Modal - Mobile First */}
      {showEverythingNotification && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowEverythingNotification(false)}
          />
          
          {/* Modal Content */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
            {/* Header */}
            <div className="p-6 pb-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">💡</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Better Value Available!
                    </h3>
                  </div>
                </div>
                <button
                  onClick={() => setShowEverythingNotification(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Pricing Comparison */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-6">
                <div className="text-center">
                  <p className="text-gray-700 text-base leading-relaxed mb-4">
                    Your current selection costs <span className="font-bold text-red-600">${totalAmount}</span> but you can get the <span className="font-bold text-blue-600">Everything Package</span> for only <span className="font-bold text-green-600">${getEverythingPackagePrice()}</span>{hasSubscriptionPackages && !hasOneTimePackages && `/${billingCycle.toLowerCase()}`}
                  </p>
                  <div className="flex items-center justify-center space-x-2 text-sm text-green-700 font-medium">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Access to ALL tracks included!</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="p-6 pt-0 space-y-3">
              <button
                onClick={() => {
                  // Find everything package with more robust search
                  const everythingPackage = packages.find(pkg =>
                    pkg.packageCode === 'EVERYTHING' ||
                    pkg.packageCode === 'everything' ||
                    pkg.name.toLowerCase().includes('everything')
                  );
                  
                  if (everythingPackage) {
                    console.log('Switching to everything package:', everythingPackage);
                    setSelectedPackages([everythingPackage.id]);
                    setShowEverythingNotification(false);
                  } else {
                    console.error('Everything package not found in packages:', packages);
                  }
                }}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 px-6 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
              >
                Switch to Everything Package
              </button>
              
              <button
                onClick={() => setShowEverythingNotification(false)}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-6 rounded-xl font-medium transition-colors"
              >
                Keep Current Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Available Packages: the pricing page's cards, picked with Select before checkout */}
      {relevantPackages.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(17rem,22rem))] md:justify-center gap-8 mb-8 pt-4">
          {relevantPackages.map((pkg: any) => {
            // Handle pricing based on billing model - check both lowercase and GraphQL enum variants
            const isOneTime = pkg.billingModel === 'one_time' || pkg.billingModel === 'ONE_TIME';
            const price = isOneTime ? pkg.priceOneTime : (billingCycle === 'WEEKLY' ? pkg.priceWeekly : pkg.priceMonthly);
            const isSelected = selectedPackages.includes(pkg.id);
            const popular = isPopularPlan(pkg.packageCode);
            const features = compact
              ? []
              : [
                  ...(pkg.packageTagRules ?? []).map((rule: any) => rule?.tag?.name).filter(Boolean),
                  ...(isOneTime && pkg.accessDurationDays ? [`${pkg.accessDurationDays} days access`] : []),
                ];

            return (
              <PlanCard
                key={pkg.id}
                name={pkg.name}
                price={price}
                period={isOneTime ? 'once' : billingCycle.toLowerCase()}
                description={
                  pkg.packageCode === 'one_track' && context?.trackName
                    ? `Get every tip for ${context.trackName} — ${pkg.description}`
                    : pkg.description
                }
                features={features}
                popular={popular}
                selected={isSelected}
                onCardClick={() => togglePackageSelection(pkg.id)}
              >
                <button
                  type="button"
                  aria-pressed={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePackageSelection(pkg.id);
                  }}
                  className={`w-full px-6 py-3 rounded-lg font-medium transition-colors text-white ${
                    isSelected || popular ? 'bg-racing-green hover:bg-racing-green-dark' : 'bg-deep-navy hover:bg-deep-navy-dark'
                  }`}
                >
                  {isSelected ? '✓ Selected' : 'Select'}
                </button>
              </PlanCard>
            );
          })}
        </div>
      )}

      {/* Active Subscriptions */}
      {ownedSubscriptions.length > 0 && (
        <div className="mb-8">
          <h4 className="text-lg font-semibold mb-4 text-center" style={{ color: '#2e7d32' }}>
            ✅ Your Active Subscriptions
          </h4>
          <div className={`grid gap-4 mb-6 ${compact ? 'max-h-96 overflow-y-auto' : ''}`} style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
            {ownedSubscriptions.map((userSub: any) => {
              // Get the package details from the subscription
              const pkg = userSub.package;
              if (!pkg) return null;
              
              const isOneTime = pkg.billingModel === 'one_time' || pkg.billingModel === 'ONE_TIME';
              const actualBillingCycle = userSub.billingCycle || 'MONTHLY';
              const actualPrice = userSub.currentPrice || (isOneTime ? pkg.priceOneTime : (actualBillingCycle === 'WEEKLY' ? pkg.priceWeekly : pkg.priceMonthly));
              const renewalDate = userSub.endDate ? new Date(userSub.endDate).toLocaleDateString('en-AU', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              }) : null;

              // Calculate expiration for one-time purchases
              const expirationDate = isOneTime && userSub.startDate && pkg.accessDurationDays ?
                new Date(new Date(userSub.startDate).getTime() + (pkg.accessDurationDays * 24 * 60 * 60 * 1000)).toLocaleDateString('en-AU', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                }) : null;

              return (
                <div
                  key={`${userSub.id}-${pkg.id}`}
                  className="bg-white text-center p-8 rounded-lg border-2 opacity-75"
                  style={{
                    borderColor: '#4CAF50',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  {/* Billing Model Badge */}
                  <div className="mb-2">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                      isOneTime ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {isOneTime ? '⏱️ One-Time' : '🔄 Subscription'}
                    </span>
                  </div>

                  {/* Package Icon and Name */}
                  <div className="mb-4">
                    <span className="text-2xl block mb-2">{getPackageIcon(pkg.packageCode)}</span>
                    <h4 className="font-bold text-lg" style={{ color: '#2d3748' }}>
                      {pkg.packageCode === 'one_track' && userSub.selectedTracks && userSub.selectedTracks.length > 0
                        ? userSub.selectedTracks[0].trackName || pkg.name
                        : pkg.name
                      }
                    </h4>
                    <div className="text-sm font-medium mt-1" style={{ color: '#2e7d32' }}>
                      ✅ Active {isOneTime ? '' : `- ${actualBillingCycle.toLowerCase()}`}
                    </div>
                  </div>

                  {/* Pricing - Show actual subscription price */}
                  <div className="mb-4">
                    {isOneTime ? (
                      <>
                        <div className="flex items-baseline justify-center space-x-1">
                          <span className="text-2xl font-bold" style={{ color: '#2d3748' }}>${actualPrice}</span>
                        </div>
                        {expirationDate && (
                          <div className="text-sm mt-2" style={{ color: '#4a5568' }}>
                            Expires: {expirationDate}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex items-baseline justify-center space-x-1">
                          <span className="text-2xl font-bold" style={{ color: '#2d3748' }}>${actualPrice}</span>
                          <span className="text-sm" style={{ color: '#718096' }}>/{actualBillingCycle.toLowerCase()}</span>
                        </div>
                        {renewalDate && (
                          <div className="text-sm mt-2" style={{ color: '#4a5568' }}>
                            Renews: {renewalDate}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Track Access Preview - Compact */}
                  {!compact && pkg.packageTagRules && pkg.packageTagRules.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1 justify-center">
                        {pkg.packageTagRules.slice(0, 2).map((rule: any) => (
                          <span
                            key={rule.id}
                            className="px-2 py-1 text-xs border rounded"
                            style={{
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              borderColor: '#45a049',
                              fontSize: '0.75rem'
                            }}
                          >
                            {rule.tag.name}
                          </span>
                        ))}
                        {pkg.packageTagRules.length > 2 && (
                          <span
                            className="px-2 py-1 text-xs border rounded"
                            style={{
                              backgroundColor: '#f7fafc',
                              borderColor: '#e2e8f0',
                              color: '#4a5568',
                              fontSize: '0.75rem'
                            }}
                          >
                            +{pkg.packageTagRules.length - 2}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Description - Only show if not compact and space allows */}
                  {!compact && (
                    <p className="text-xs text-center mt-2" style={{ color: '#4a5568', lineHeight: '1.4' }}>
                      {pkg.packageCode === 'one_track' ? (
                        userSub.selectedTracks && userSub.selectedTracks.length > 0
                          ? `Get every tip for ${userSub.selectedTracks[0].trackName} — ${pkg.description}`
                          : pkg.description
                      ) : pkg.description}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Desktop Continue Button - always visible and prominent */}
      <div className="hidden md:block text-center mb-8">
        <Button
          onClick={handleSubscribeClick}
          disabled={selectedPackages.length === 0}
          className={`px-10 py-5 text-xl font-bold shadow-xl transition-all duration-200 transform ${
            selectedPackages.length > 0
              ? 'bg-green-600 hover:bg-green-700 text-white hover:shadow-2xl hover:scale-105'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {selectedPackages.length > 0
            ? `${hasOneTimePackages && !hasSubscriptionPackages ? 'Buy Now' : 'Subscribe Now'} - $${totalAmount}${hasSubscriptionPackages && !hasOneTimePackages ? `/${billingCycle.toLowerCase()}` : ''}`
            : 'Select a package to continue'
          }
        </Button>
      </div>

      {/* Mobile Sticky Continue Button - always visible at bottom */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
        <div className="p-4 pb-safe">
          <Button
            onClick={handleSubscribeClick}
            disabled={selectedPackages.length === 0}
            className={`w-full px-6 py-4 text-lg font-bold shadow-lg transition-all duration-200 ${
              selectedPackages.length > 0
                ? 'bg-green-500 hover:bg-green-600 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {selectedPackages.length > 0
              ? `${hasOneTimePackages && !hasSubscriptionPackages ? 'Buy Now' : 'Subscribe Now'} - $${totalAmount}${hasSubscriptionPackages && !hasOneTimePackages ? `/${billingCycle.toLowerCase()}` : ''}`
              : 'Select a package to continue'
            }
          </Button>
        </div>
      </div>

      {/* Mobile spacing to prevent content being hidden behind sticky button */}
      <div className="md:hidden h-20"></div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        selectedPackages={selectedPackages}
        billingCycle={billingCycle}
        totalAmount={totalAmount}
        onSuccess={handleModalPaymentSuccess}
        selectedTrackId={context?.trackId}
        packages={relevantPackages.map(pkg => ({
          id: pkg.id.toString(),
          name: pkg.name,
          billingModel: pkg.billingModel,
          priceWeekly: pkg.priceWeekly,
          priceMonthly: pkg.priceMonthly,
          priceOneTime: pkg.priceOneTime,
          accessDurationDays: pkg.accessDurationDays
        }))}
      />
    </>
  );
};